'use client';

import React, { createContext, useContext, type ReactNode } from 'react';
import { useDraftRequest } from './use-draft-request';

type DraftRequestReturn = ReturnType<typeof useDraftRequest>;

const DraftRequestContext = createContext<DraftRequestReturn | null>(null);

/**
 * Provider component wrapping the useDraftRequest logic for global access.
 *
 * NOTE: This provider must be added to the app's providers.tsx,
 * wrapping it inside GraphQLProvider so Apollo Client is available:
 *
 *   <SessionProvider>
 *     <GraphQLProvider>
 *       <CartProvider>
 *         {children}
 *       </CartProvider>
 *     </GraphQLProvider>
 *   </SessionProvider>
 */
export function CartProvider({ children }: { children: ReactNode }) {
  const draftRequest = useDraftRequest();
  return (
    <DraftRequestContext.Provider value={draftRequest}>
      {children}
    </DraftRequestContext.Provider>
  );
}

/**
 * Access the draft-request (cart) state from any component inside CartProvider.
 * Replaces the prototype's useCart() from CartContext.
 */
export function useCart() {
  const ctx = useContext(DraftRequestContext);
  if (!ctx) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return ctx;
}
